// <copyright file="Dashboards.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import * as QuicksightEmbedding from "amazon-quicksight-embedding-sdk";
import axios from "axios";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useState as _useState, useContext, useEffect } from "react";
import ComponentMenu from "src/components/ComponentMenu/ComponentMenu";
import CvdfVisualizer from "src/components/CvdfVisualizer/CvdfVisualizer";
import EsuReport from "src/components/EsuReport/EsuReport";
import RsuCoverage from "src/components/VIPER/RsuCoverage/RsuCoverage";
import ConfigContext from "src/context/ConfigContext";
import {
    LoadingStatus,
    Permission,
    checkConfigSectionFlag,
    checkFeatFlagandPermissions,
    checkUserPermission,
    displayLoadingError,
    getCookieJsonField,
} from "src/utils/SharedUtils";
import { v4 as uuidv4 } from "uuid";
import type { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import type { FC } from "react";
import type { IConfig } from "src/utils/ConfigLoader";

// Properties to initialize this UI component
interface DashboardsProps {
    useState?: typeof _useState; // optional hook override (for tests)
}

// Data model for QuickSight dashboard information retrieved from AWS
export interface QuickSightDashboard {
    DashboardId: string;
    Name: string;
}

// Data model for QuickSight dashboard information retrieved from AWS
export interface QuickSightDashboardInfo {
    Dashboards: QuickSightDashboard[];
    EmbedUrl: string;
}

// Info/error message popup
const toast = React.createRef<Toast>();

// Main content focus item
const mainContentRef = React.createRef<HTMLAnchorElement>();

/**
 * Embeds a QuickSight dashboard view into a react web component
 * @param embedUrl Url of the initial dashboard to embed
 * @param setEmbeddedDashboard Function that sets the state of the embedded QuickSight dashboard
 * @param setQsLoadingStatus Function that sets the state of QuickSight loading status
 */
function embedQuickSightDashboard(
    embedUrl: string,
    setEmbeddedDashboard: React.Dispatch<React.SetStateAction<unknown>>,
    setQsLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>
) {
    const container = document.getElementById("QsEmbeddedDashboard");
    if (embedUrl === "") {
        return <ProgressSpinner className="App-spinner" aria-label="Loading" />;
    } else if (
        container &&
        container.getElementsByClassName("quicksight-embedding-iframe").length >
            0
    ) {
        return; // Ensure dashboard embedding only happens once
    }
    console.debug("Embedding QuickSight...");
    const params = {
        // https://github.com/awslabs/amazon-quicksight-embedding-sdk?tab=readme-ov-file#dashboard-embedding
        url: embedUrl,
        container: container,
        undoRedoDisabled: true,
        resetDisabled: true,
        scrolling: "no",
        height: "AutoFit",
        iframeResizeOnSheetChange: true, // use this option in combination with height: AutoFit, to allow iframe height to resize dynamically, based on sheet height, on changing sheets.
        width: "100%",
        locale: "en-US",
        footerPaddingEnabled: false,
        sheetTabsDisabled: false, // use this option to enable or disable sheet tab controls in dashboard embedding
        printEnabled: true,
    };
    try {
        const embedded = QuicksightEmbedding.embedDashboard(params);
        const iframe = embedded.iframe as HTMLIFrameElement;
        if (iframe) {
            // Style iframe to fill parent container element with flex:column
            iframe.style.flexGrow = "1";
            iframe.style.border = "none";
            iframe.style.margin = "0";
            iframe.style.padding = "0";
        }
        console.debug(`Embedded iframe: ${iframe.outerHTML}`);
        setEmbeddedDashboard(embedded);
    } catch (error) {
        const summary = "Error embedding QuickSight";
        const detail =
            error instanceof Error ? error.message : "Unknown error type";
        console.debug(`${summary}: ${detail}`);
        if (
            detail.startsWith("Failed to read dashboard") &&
            detail.endsWith("dashboardId:dashboardId")
        ) {
            // Bypass error thrown on initial embedding with placeholder dashboard id
        } else {
            displayLoadingError(
                summary,
                detail,
                toast.current,
                setQsLoadingStatus
            );
        }
    }
}

/**
 * Navigates embedded QuickSight dashboard view to a specific dashboard
 * @param dashboardId Id of the dashboard to display
 * @param qsEmbeddedDashboard Embedded QuickSight dashboard object
 */
function navigateQuickSightDashboard(
    dashboardId: string,
    // eslint disabled on line below because QuickSight embedded dashboard object has `any` type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qsEmbeddedDashboard: any
) {
    console.debug(`Navigating to QS dashboard id ${dashboardId}`);
    if (qsEmbeddedDashboard === null) {
        console.debug("Embedded dashboard is null");
    } else {
        qsEmbeddedDashboard?.navigateToDashboard({ dashboardId });
    }
}

/**
 * Update component menu items with retrieved QuickSight dashboard information
 * @param menuItems Menu items for the component
 * @param dashboards QuickSight dashboards visible in the component
 * @param setMenuSelected Function that sets the state of selected menu item for this component
 * @param setQsIsDisplayed Function that sets the state of QuickSight embedded dashboard visibilty
 * @param setVpIsDisplayed Function that sets the state of VIPER embedded dashboard visibilty
 * @param setMenuItems Function that sets the state of menu items for this component
 * @param qsEmbeddedDashboard Embedded QuickSight dashboard object
 */
function setQuickSightMenuItems(
    menuItems: MenuItem[],
    dashboards: QuickSightDashboard[],
    setMenuSelected: React.Dispatch<React.SetStateAction<string>>,
    setQsIsDisplayed: React.Dispatch<React.SetStateAction<boolean>>,
    setVpIsDisplayed: React.Dispatch<React.SetStateAction<boolean>>,
    setMenuItems: React.Dispatch<React.SetStateAction<MenuItem[]>>,
    // eslint disabled on line below because QuickSight embedded dashboard object has `any` type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qsEmbeddedDashboard: any
) {
    // Update quicksight menu items
    console.debug(`Adding ${dashboards.length} QS dashboard items`);
    const qsItemsIdx = menuItems.findIndex(
        (i) => i.label === "QuickSight Dashboards"
    );
    const qsItemsNew: MenuItem[] = [];
    dashboards.forEach((dash: QuickSightDashboard) => {
        console.debug(`Adding menu item for QS dashboard ${dash.Name}`);
        qsItemsNew.push({
            label: dash.Name,
            command: () => {
                setMenuSelected(dash.Name);
                setQsIsDisplayed(true);
                setVpIsDisplayed(false);
                navigateQuickSightDashboard(
                    dash.DashboardId,
                    qsEmbeddedDashboard
                );
                mainContentRef.current?.focus();
            },
        });
    });
    if (qsItemsIdx < 0) {
        // Add menu section and items
        const mainItem: MenuItem = checkUserPermission(
            Permission.UIQuickSightRead
        )
            ? { label: "QuickSight", items: qsItemsNew }
            : { visible: false };
        setMenuItems([mainItem].concat(menuItems)); // Replace (not mutate) to trigger menu render
    } else {
        // Update existing menu section items
        menuItems[qsItemsIdx].items = qsItemsNew;
        setMenuItems([...menuItems]); // Replace (not mutate) to trigger menu render
    }
}

/**
 * Retrieves QuickSight dashboard information
 * @param qsLoadingStatus State of data retrieval from QuickSight
 * @param setQsLoadingStatus Function that sets the state of data retrieval from QuickSight
 * @param setQsDashboards Function that sets the state of the viewable QuickSight dashboards
 * @param setEmbeddedDashboard Function that sets the state of the embedded QuickSight dashboard
 */
function fetchQuickSightDashboardUrls(
    qsLoadingStatus: LoadingStatus,
    configData: IConfig,
    setQsLoadingStatus: React.Dispatch<React.SetStateAction<LoadingStatus>>,
    setQsDashboards: React.Dispatch<
        React.SetStateAction<QuickSightDashboard[]>
    >,
    setEmbeddedDashboard: React.Dispatch<React.SetStateAction<unknown>>
) {
    if (
        qsLoadingStatus !== LoadingStatus.preload ||
        !checkFeatFlagandPermissions(
            "dashboards-quicksight",
            configData.featureFlags,
            Permission.UIQuickSightRead
        )
    ) {
        return;
    }
    console.debug("Fetching QuickSight dashboard data...");
    setQsLoadingStatus(LoadingStatus.loading);
    const headers = {
        headers: {
            Authorization: `Bearer ${getCookieJsonField(
                "DepSessionIdentity",
                "token"
            )}`,
        },
    };
    axios
        .get<QuickSightDashboardInfo>(
            `${configData.apiBaseUrl}/quicksight-dashboards`,
            headers
        )
        .then((response) => {
            response.data.Dashboards.forEach((dash) => {
                console.debug(`Response dashboard: ${dash.Name}`);
            });
            console.debug(`Response embed url: ${response.data.EmbedUrl}`);
            setQsDashboards(response.data.Dashboards);
            // If dashboards found, use the first id in the embedding url; prevents
            // browser errors due to embedding url with generic placeholder id
            const embedUrl =
                response.data.Dashboards.length > 0
                    ? response.data.EmbedUrl.replace(
                          "dashboardId",
                          response.data.Dashboards[0].DashboardId
                      )
                    : response.data.EmbedUrl;
            embedQuickSightDashboard(
                embedUrl,
                setEmbeddedDashboard,
                setQsLoadingStatus
            );
            setQsLoadingStatus(LoadingStatus.loaded);
        })
        .catch((error) => {
            const summary = "Error fetching QuickSight dashboards";
            const detail = error.response
                ? error.response.data.errorMessage
                : error.message;
            displayLoadingError(
                summary,
                detail,
                toast.current,
                setQsLoadingStatus
            );
        });
}

/**
 * Determines whether to display a progress spinner to indicate data loading, based on the
 * status of data loads and current user view selection
 * @param sourceSelected A user-selected dashboard source; set when the user selects a specific dashboard
 * @param qsLoadingStatus Status of QuickSight dashboard data load
 * @param vpLoadingStatus Status of VIPER dashboard data load
 * @returns Indicator whether the progress spinner should be displayed
 */
function shouldDisplayLoadingSpinner(
    sourceSelected: string | null,
    qsLoadingStatus: LoadingStatus,
    vpLoadingStatus: LoadingStatus
) {
    return (
        [qsLoadingStatus, vpLoadingStatus].includes(LoadingStatus.loading) &&
        [null, "QuickSight Dashboards"].includes(sourceSelected)
    );
}

/**
 * Determines whether to display an error message element after data loading issues are encountered
 * @param sourceSelected A user-selected dashboard source; set when the user selects a specific dashboard
 * @param qsLoadingStatus Status of QuickSight dashboard data load
 * @param vpLoadingStatus Status of VIPER dashboard data load
 * @returns Indicator whether the error message element should be displayed
 */
function shouldDisplayLoadingError(
    sourceSelected: string | null,
    qsLoadingStatus: LoadingStatus,
    vpLoadingStatus: LoadingStatus
) {
    return (
        (sourceSelected == null &&
            [qsLoadingStatus, vpLoadingStatus].includes(LoadingStatus.error)) ||
        (sourceSelected === "QuickSight Dashboards" &&
            qsLoadingStatus === LoadingStatus.error) ||
        (sourceSelected === "DEP Dashboards" &&
            vpLoadingStatus === LoadingStatus.error)
    );
}

/**
 * Function to decide whether to render the RSU Coverage Component
 * @param vpIsDisplayed Whether Vp is displayed
 * @param menuSelected current menu item string
 * @param featureFlags Config featureFlags section
 * @returns boolean whether to render the RSU Coverage Component
 */
function shouldDisplayRsuCoverage(
    vpIsDisplayed: boolean,
    menuSelected: string,
    featureFlags: { name: string; enabled: boolean }[]
) {
    const featConfig = checkFeatFlagandPermissions(
        "dashboards-dep-rsu-coverage",
        featureFlags,
        Permission.UIRsuCoverageRead
    );
    return vpIsDisplayed && menuSelected === "RSU Coverage" && featConfig;
}

/**
 * Function to decide whether to render the ESU Report Component
 * @param vpIsDisplayed Whether Vp is displayed
 * @param menuSelected current menu item string
 * @param featureFlags Config featureFlags section
 * @returns boolean whether to render the ESU Report Component
 */
function shouldDisplayEsuReport(
    vpIsDisplayed: boolean,
    menuSelected: string,
    featureFlags: { name: string; enabled: boolean }[]
) {
    const featConfig = checkFeatFlagandPermissions(
        "dashboards-dep-esu-report",
        featureFlags,
        Permission.UIRsuCoverageRead
    );
    return vpIsDisplayed && menuSelected === "ESU Report" && featConfig;
}

/**
 * Function to decide whether to render the CVDF Visualizer Component
 * @param vpIsDisplayed Whether Vp is displayed
 * @param menuSelected current menu item string
 * @param featureFlags Config featureFlags section
 * @returns boolean whether to render the ESU Report Component
 */
function shouldDisplayCvdfVisualizer(
    vpIsDisplayed: boolean,
    menuSelected: string,
    featureFlags: { name: string; enabled: boolean }[]
) {
    const featConfig = checkFeatFlagandPermissions(
        "dashboards-dep-cvdf-visualizer",
        featureFlags,
        Permission.UIRsuCoverageRead
    );
    return vpIsDisplayed && menuSelected === "CVDF Visualizer" && featConfig;
}

/**
 * Component to select/view embedded dashboards from multiple sources
 * @param props Component props
 * @returns The dashboard select/view component
 */
const Dashboards: FC<DashboardsProps> = (props: DashboardsProps) => {
    const configData = useContext(ConfigContext) as IConfig;
    const useState = props.useState ?? _useState;
    const menuRef = React.useRef<Menu>(null);

    // Component menu fields
    const [menuSelected, setMenuSelected] = useState(
        "Please select a dashboard to view"
    );
    const [menuItems, setMenuItems] = useState<MenuItem[]>([
        checkFeatFlagandPermissions(
            "dashboards-quicksight",
            configData.featureFlags,
            Permission.UIQuickSightRead
        )
            ? {
                  label: "QuickSight Dashboards",
                  icon: "pi-chart-bar",
                  items: [],
              }
            : { visible: false },
        checkConfigSectionFlag("dashboards-dep", configData.featureFlags)
            ? {
                  label: "DEP Dashboards",
                  icon: "pi-map-marker",
                  items: [
                      checkFeatFlagandPermissions(
                          "dashboards-dep-rsu-coverage",
                          configData.featureFlags,
                          Permission.UIRsuCoverageRead
                      )
                          ? {
                                label: "RSU Coverage",
                                command: () => {
                                    setMenuSelected("RSU Coverage");
                                    setQsIsDisplayed(false);
                                    setVpIsDisplayed(true);
                                    mainContentRef.current?.focus();
                                },
                            }
                          : { visible: false },
                          checkFeatFlagandPermissions(
                            "dashboards-dep-esu-report",
                            configData.featureFlags,
                            Permission.UIEsuReportRead
                          )
                          ? {
                                label: "ESU Report",
                                command: () => {
                                    setMenuSelected("ESU Report");
                                    setQsIsDisplayed(false);
                                    setVpIsDisplayed(true);
                                    mainContentRef.current?.focus();
                                },
                            } : { visible: false },
                          checkFeatFlagandPermissions(
                            "dashboards-dep-cvdf-visualizer",
                            configData.featureFlags,
                            Permission.UICvdfVisualizerRead
                          )
                          ?
                            {
                                label: "CVDF Visualizer",
                                command: () => {
                                    setMenuSelected("CVDF Visualizer");
                                    setQsIsDisplayed(false);
                                    setVpIsDisplayed(true);
                                    mainContentRef.current?.focus();
                                },
                            }
                            : { visible: false }
                  ],
              }
            : { visible: false },
    ]);

    // QuickSight dashboard fields
    const [qsLoadingStatus, setQsLoadingStatus] = useState(
        LoadingStatus.preload
    );
    const [qsIsDisplayed, setQsIsDisplayed] = useState(false);
    const [qsDashboards, setQsDashboards] = useState<QuickSightDashboard[]>([]);
    const [qsEmbeddedDashboard, setEmbeddedDashboard] = useState<unknown>(null);

    // VIPER dashboard fields
    // eslint disabled on line below pending implementation of VIPER embedded dashboards
    // eslint-disable-next-line autofix/no-unused-vars, @typescript-eslint/no-unused-vars
    const [vpLoadingStatus, _setVpLoadingStatus] = useState(
        LoadingStatus.preload
    );
    const [vpIsDisplayed, setVpIsDisplayed] = useState(false);

    /**
     * Hook on component mount/unmount
     */
    useEffect(() => {
        fetchQuickSightDashboardUrls(
            qsLoadingStatus,
            configData,
            setQsLoadingStatus,
            setQsDashboards,
            setEmbeddedDashboard
        );
    }, []);

    /**
     * Hook on QuickSight dashboards changed
     */
    useEffect(() => {
        if (
            checkFeatFlagandPermissions(
                "dashboards-quicksight",
                configData.featureFlags,
                Permission.UIQuickSightRead
            )
        ) {
            setQuickSightMenuItems(
                menuItems,
                qsDashboards,
                setMenuSelected,
                setQsIsDisplayed,
                setVpIsDisplayed,
                setMenuItems,
                qsEmbeddedDashboard
            );
        }
    }, [qsDashboards]);

    // Find source of selected dashboard
    let sourceSelected = null;
    for (const source of menuItems.flatMap((i) => i.label ?? "unknown")) {
        const nestedItems =
            menuItems.find((i) => i.label === source)?.items?.flat() ?? [];
        if (nestedItems.findIndex((i) => i.label === menuSelected) >= 0) {
            sourceSelected = source;
            break;
        }
    }

    const linkDashboards = (
        <a ref={mainContentRef} href="/dashboards">
            Dashboards
        </a>
    );
    const breadcrumb = sourceSelected
        ? `> ${sourceSelected} : ${menuSelected}`
        : `> ${menuSelected}`;

    // Create the react component
    // Quicksight embed div must be present but can be hidden, so add it last
    return (
        <div className="App-content" data-testid="Dashboards">
            <Toast ref={toast} />
            <ComponentMenu
                focusRef={menuRef}
                title="Dashboards"
                menuItems={menuItems}
            />
            <div className="App-content-main">
                <div id="breadcrumb" className="flex flex-row gap-2 mb-2">
                    {linkDashboards} {breadcrumb}
                </div>
                {shouldDisplayLoadingSpinner(
                    sourceSelected,
                    qsLoadingStatus,
                    vpLoadingStatus
                ) && <ProgressSpinner />}
                {shouldDisplayLoadingError(
                    sourceSelected,
                    qsLoadingStatus,
                    vpLoadingStatus
                ) && <div className="App-content-error" />}
                {configData?.apiBaseUrl &&
                    shouldDisplayRsuCoverage(
                        vpIsDisplayed,
                        menuSelected,
                        configData.featureFlags
                    ) && (
                        <RsuCoverage
                            apiBaseUrl={configData.apiBaseUrl ?? "unknown"}
                        />
                    )}
                {configData?.apiBaseUrl &&
                    shouldDisplayEsuReport(
                        vpIsDisplayed,
                        menuSelected,
                        configData.featureFlags
                    ) && (
                        <EsuReport
                            apiBaseUrl={configData.apiBaseUrl ?? "unknown"}
                            baseUrl={configData.loginRedirectUrl ?? "unknown"}
                        />
                    )}
                {configData?.apiBaseUrl &&
                    shouldDisplayCvdfVisualizer(
                        vpIsDisplayed,
                        menuSelected,
                        configData.featureFlags
                    ) && (
                        <CvdfVisualizer
                            key={uuidv4()}
                            cvdfApiBaseUrl={configData.cvdfApiBaseUrl || "unknown"}
                            cvdfRealTimeBaseUrl={configData.cvdfRealTimeBaseUrl || "unknown"}
                        />
                    )}
                <div
                    id="QsEmbeddedDashboard"
                    title="QuickSight embedded dashboard"
                    style={{
                        visibility: qsIsDisplayed ? "visible" : "hidden",
                        display: "flex",
                        width: "100%",
                        height: "100%",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                />
            </div>
        </div>
    );
};

export default Dashboards;
